import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

type MediaFile = { name: string; resource_id: string }
type Preview = { type: 'image' | 'video'; url: string }
export default function GuestMediaViewer({ items, selectedId, getHeaders, onClose }: {
  items: MediaFile[]; selectedId: string; getHeaders: () => Record<string, string>; onClose: () => void
}) {
  const [index, setIndex] = useState(Math.max(0, items.findIndex(file => file.resource_id === selectedId)))
  const [preview, setPreview] = useState<Preview | null>(null)
  const [error, setError] = useState('')
  const video = useRef<HTMLVideoElement>(null)
  const dialog = useRef<HTMLElement>(null)
  const auth = useRef(getHeaders)
  auth.current = getHeaders
  const touch = useRef<{ x: number; y: number } | null>(null)
  const file = items[index]
  const navigate = (offset: number) => {
    const next = index + offset
    if (next < 0 || next >= items.length) return
    video.current?.pause()
    setPreview(null); setError(''); setIndex(next)
  }
  const close = () => { video.current?.pause(); onClose() }
  useEffect(() => {
    const focused = document.activeElement as HTMLElement | null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialog.current?.focus()
    return () => { document.body.style.overflow = overflow; focused?.focus() }
  }, [])
  useEffect(() => {
    if (!file) return
    const controller = new AbortController()
    const timer = window.setTimeout(() => { setError('La nube tardó demasiado en responder. Cierra y vuelve a abrir el archivo.'); controller.abort() }, 20000)
    setPreview(null); setError('')
    void (async () => {
      try {
        const response = await fetch('/api/file/open', {
          method: 'POST', signal: controller.signal,
          headers: { ...auth.current(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ resource_id: file.resource_id, public_base: window.location.origin })
        })
        if (!response.ok) throw new Error(`No se pudo abrir el archivo (${response.status}).`)
        const data = await response.json()
        if (!['image', 'video'].includes(data.type) || !(data.url || data.raw_url)) throw new Error('Este formato no tiene vista previa de foto o video disponible.')
        const url = new URL(data.raw_url || data.url, window.location.origin)
        if (url.origin !== window.location.origin) throw new Error('La vista previa no apunta a tu servidor Nexus.')
        if (!controller.signal.aborted) setPreview({ type: data.type, url: url.href })
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : 'No se pudo abrir.')
      } finally { window.clearTimeout(timer) }
    })()
    return () => { controller.abort(); window.clearTimeout(timer) }
  }, [file?.resource_id])
  useEffect(() => {
    const current = video.current
    return () => { current?.pause(); current?.removeAttribute('src'); current?.load() }
  }, [preview?.url])
  if (!file) return null
  return createPortal(<section className="guest-media-viewer" ref={dialog} role="dialog" aria-modal="true" aria-label={file.name} tabIndex={-1}
    onKeyDown={event => {
      if (event.key === 'Escape') close()
      if (event.key === 'ArrowLeft' && event.target === dialog.current) navigate(-1)
      if (event.key === 'ArrowRight' && event.target === dialog.current) navigate(1)
      if (event.key === 'Tab') {
        const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),video[controls]') || [])
        const first = controls[0], last = controls[controls.length - 1]
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus() }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
      }
    }}>
    <header><button type="button" title="Volver a Mi nube" aria-label="Volver a Mi nube" onClick={close}><X size={22} /></button><span title={file.name}>{file.name}</span><small>{index + 1} / {items.length}</small></header>
    <div className="guest-media-stage" onTouchStart={event => {
      if (event.touches.length !== 1) { touch.current = null; return }
      const point = event.touches[0]
      if (!point) { touch.current = null; return }
      if (video.current && point.clientY > video.current.getBoundingClientRect().bottom - 64) { touch.current = null; return }
      touch.current = { x: point.clientX, y: point.clientY }
    }} onTouchCancel={() => { touch.current = null }} onTouchEnd={event => {
      const point = event.changedTouches[0], start = touch.current
      touch.current = null
      if (!point || !start) return
      const dx = point.clientX - start.x, dy = point.clientY - start.y
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) navigate(dx < 0 ? 1 : -1)
    }}>
      {error ? <p role="alert">{error}</p> : !preview ? <p role="status">Abriendo desde tu nube…</p> : preview.type === 'video'
        ? <video key={file.resource_id} ref={video} src={preview.url} controls playsInline preload="metadata" autoPlay={false} onError={() => setError('No se pudo reproducir este video. Comprueba su formato y conexión.')} />
        : <img key={file.resource_id} src={preview.url} alt={file.name} draggable={false} onError={() => setError('No se pudo cargar la fotografía. Comprueba su formato y conexión.')} />}
    </div>
    <footer><button type="button" title="Anterior" aria-label="Anterior" disabled={index === 0} onClick={() => navigate(-1)}><ChevronLeft size={24} /></button><span>{index + 1} / {items.length}</span><button type="button" title="Siguiente" aria-label="Siguiente" disabled={index === items.length - 1} onClick={() => navigate(1)}><ChevronRight size={24} /></button></footer>
  </section>, document.body)
}
